import React from 'react'
import { Spin } from 'antd'
import {
  ArrowRightOutlined,
  BankOutlined,
  HistoryOutlined,
  LockOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import type { AuditLogEntry } from '../api/access-control.client'
import {
  toAccessHistoryItem,
  type AccessHistoryItem,
} from '../utils/accessHistory'
import styles from './UserAccessHistory.module.scss'

interface Props {
  entries: AuditLogEntry[]
  total: number
  isLoading: boolean
  isError: boolean
}

function RoleChips({
  roles,
  implied,
  muted = false,
}: {
  roles: string[]
  implied: string | null
  muted?: boolean
}) {
  if (implied) {
    return <span className={styles.impliedChip}>Implied: {implied}</span>
  }
  if (roles.length === 0) {
    return <span className={styles.mutedChip}>No role</span>
  }
  return (
    <>
      {roles.map((role) => (
        <span key={role} className={muted ? styles.mutedChip : styles.roleChip}>
          {role}
        </span>
      ))}
    </>
  )
}

function ItemIcon({ kind }: { kind: AccessHistoryItem['kind'] }) {
  if (kind === 'companyAccess') {
    return <BankOutlined />
  }
  if (kind === 'other') {
    return <HistoryOutlined />
  }
  return <LockOutlined />
}

export default function UserAccessHistory({
  entries,
  total,
  isLoading,
  isError,
}: Props) {
  if (isLoading) {
    return (
      <div className={styles.state}>
        <Spin />
      </div>
    )
  }
  if (isError) {
    return (
      <div className={styles.state}>Couldn&apos;t load history. Try again.</div>
    )
  }
  if (entries.length === 0) {
    return <div className={styles.state}>No access changes recorded yet.</div>
  }

  const items = entries.map(toAccessHistoryItem)
  return (
    <div>
      <ol className={styles.timeline}>
        {items.map((item) => (
          <li key={item.id} className={styles.item}>
            <span className={styles.icon}>
              <ItemIcon kind={item.kind} />
            </span>
            <div className={styles.body}>
              <span className={styles.time}>
                {dayjs(item.timestamp).format('MMM D, YYYY · h:mm A')}
              </span>
              <span className={styles.title}>{item.title}</span>
              {item.kind === 'rolesUpdated' && (
                <div className={styles.chips}>
                  <RoleChips
                    roles={item.fromRoles ?? []}
                    implied={item.fromImplied}
                    muted
                  />
                  <ArrowRightOutlined className={styles.arrow} />
                  <RoleChips roles={item.roles} implied={item.toImplied} />
                </div>
              )}
              {item.description && (
                <span className={styles.description}>{item.description}</span>
              )}
              {item.reason && (
                <span className={styles.reason}>“{item.reason}”</span>
              )}
              <span className={styles.actor}>
                by <strong>{item.actor}</strong>
              </span>
            </div>
          </li>
        ))}
      </ol>
      {total > entries.length && (
        <p className={styles.more}>
          Showing the latest {entries.length} of {total} changes.
        </p>
      )}
    </div>
  )
}
